import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { RequalBurnDown } from "@/types"

import type { RequalSeries, RequalSeriesPoint } from "./types"

// The requal burn-down: cumulative completions against a straight line to the
// program target.
//
// THIS IS THE EMPHASIS FORM, not a two-series categorical chart. Completions
// are the story and wear the one accent hue; the target is context and wears
// the de-emphasis gray, dashed. Dashing the reference line is the secondary
// encoding that keeps the two apart without a second hue (gridlines stay solid
// hairlines — a dashed GRIDLINE is noise, a dashed target line is a convention).
//
// Colors are set as CSS custom properties in a scoped style block so light and
// dark each get a step chosen for their own surface, rather than one palette
// flipped automatically. Both steps were validated against their surface.

// Only the DATA hues are literals — each is a step chosen and validated for its
// own surface, which is why dark is a different hex rather than the light one
// auto-flipped. The chrome (gridlines, the ring behind the end marker) rides
// the app's own theme tokens so the chart can never drift from the card it
// sits on.
const SERIES_STYLE = `
.rr-burndown {
  --rr-complete: #2a78d6;
  --rr-target: #8a8984;
  --rr-grid: var(--border);
  --rr-surface: var(--card);
}
.dark .rr-burndown {
  --rr-complete: #3987e5;
  --rr-target: #6f6e69;
}
`

interface TooltipPayloadItem {
  payload?: RequalSeriesPoint
}

function BurnDownTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: TooltipPayloadItem[]
}) {
  const point = payload?.[0]?.payload
  if (!active || !point) return null

  return (
    <div className="bg-popover text-popover-foreground rounded-md border px-3 py-2 text-xs shadow-md">
      <p className="font-medium">{point.label}</p>
      <p className="text-muted-foreground mt-1 tabular-nums">
        Complete: {point.complete ?? "—"}
      </p>
      <p className="text-muted-foreground tabular-nums">
        On plan: {Math.round(point.target)}
      </p>
    </div>
  )
}

// A legend is always present for two or more marks — identity never rests on
// color alone. Rendered as real text beside a colored key rather than by
// coloring the label itself.
function LegendKey({
  color,
  dashed,
  children,
}: {
  color: string
  dashed?: boolean
  children: string
}) {
  return (
    <span className="text-muted-foreground inline-flex items-center gap-2">
      <svg width="18" height="2" aria-hidden="true">
        <line
          x1="0"
          y1="1"
          x2="18"
          y2="1"
          stroke={color}
          strokeWidth="2"
          strokeDasharray={dashed ? "4 3" : undefined}
        />
      </svg>
      {children}
    </span>
  )
}

interface Props {
  burnDown: RequalBurnDown
  series: RequalSeries
}

export function RequalBurnDownChart({ burnDown, series }: Props) {
  const behind = series.ahead_behind < 0
  const gap = Math.abs(series.ahead_behind)

  return (
    <Card className="rr-burndown">
      <style>{SERIES_STYLE}</style>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle>Requal burn-down — {burnDown.program_year}</CardTitle>
          <p className="text-muted-foreground mt-1 text-sm">
            {burnDown.complete} of {burnDown.target} complete ·{" "}
            {burnDown.in_progress} in flight · {burnDown.planned} planned
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold tabular-nums">
            {burnDown.remaining}
          </div>
          <p className="text-muted-foreground text-xs">remaining</p>
        </div>
      </CardHeader>

      <CardContent>
        <div className="flex flex-wrap items-center gap-4 pb-3 text-xs">
          <LegendKey color="var(--rr-complete)">Completed</LegendKey>
          <LegendKey color="var(--rr-target)" dashed>
            On plan
          </LegendKey>
          <span
            className="text-muted-foreground ml-auto"
            data-testid="requal-pace"
          >
            {gap === 0
              ? "On plan"
              : `${gap} ${behind ? "behind" : "ahead of"} plan`}
          </span>
        </div>

        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={series.points}
              margin={{ top: 8, right: 12, bottom: 0, left: -16 }}
            >
              <CartesianGrid
                stroke="var(--rr-grid)"
                strokeWidth={1}
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={{ stroke: "var(--rr-grid)" }}
                tick={{ fontSize: 11, fill: "currentColor" }}
                className="text-muted-foreground"
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                width={44}
                tick={{ fontSize: 11, fill: "currentColor" }}
                className="text-muted-foreground"
              />
              <Tooltip
                content={<BurnDownTooltip />}
                cursor={{ stroke: "var(--rr-grid)", strokeWidth: 1 }}
              />
              {/* Context first so the accent series draws on top of it. */}
              <Line
                type="linear"
                dataKey="target"
                stroke="var(--rr-target)"
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="complete"
                stroke="var(--rr-complete)"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                // Surface ring keeps the end marker legible where it crosses
                // the target line.
                dot={false}
                activeDot={{
                  r: 4,
                  fill: "var(--rr-complete)",
                  stroke: "var(--rr-surface)",
                  strokeWidth: 2,
                }}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}
